import { GITLAB_GRAPHQL_URL } from "../constants";
import { isRetryableGraphqlErrors } from "./graphqlErrors";

export const DISCUSSION_BATCH_SIZE = 4;
const CONCURRENCY = 4;
const PAGE_SIZE = 100;
const REQUEST_TIMEOUT_MS = 15000;
const RETRY_DELAYS_MS = [400, 1000];
const inFlightQueries = new Map();

const DISCUSSION_FIELDS = `
	id
	resolvable
	resolved
	notes(last: ${PAGE_SIZE}) {
		pageInfo { hasPreviousPage }
		nodes {
			body
			bodyHtml
			createdAt
			system
			url
			position {
				newPath
				oldPath
				newLine
				oldLine
				diffRefs { headSha startSha }
			}
			author {
				bot
				avatarUrl
				name
				username
			}
		}
	}
`;

const createBatchQuery = (mrs) => `
	query getCommentAttention {
		${mrs
			.map(
				(mr, index) => `
					mergeRequest${index}: mergeRequest(id: ${JSON.stringify(mr.id)}) {
						discussions(last: ${PAGE_SIZE}) {
							pageInfo { hasPreviousPage startCursor }
							nodes { ${DISCUSSION_FIELDS} }
						}
					}
				`,
			)
			.join("\n")}
	}
`;

const createPreviousPageQuery = (mrId, cursor) => `
	query getEarlierCommentAttention {
		mergeRequest(id: ${JSON.stringify(mrId)}) {
			discussions(last: ${PAGE_SIZE}, before: ${JSON.stringify(cursor)}) {
				pageInfo { hasPreviousPage startCursor }
				nodes { ${DISCUSSION_FIELDS} }
			}
		}
	}
`;

const wait = (milliseconds) =>
	new Promise((resolve) => setTimeout(resolve, milliseconds));

const cancelledError = () =>
	new DOMException("Comment activity request cancelled", "AbortError");

// `signal` cancels the whole request, retries included. The per-attempt
// controller below only enforces the timeout, which stays retryable.
const requestQuery = async (query, token, signal) => {
	let lastError;

	for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
		if (signal?.aborted) throw cancelledError();
		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
		const cancel = () => controller.abort();
		signal?.addEventListener("abort", cancel, { once: true });

		try {
			const response = await fetch(GITLAB_GRAPHQL_URL, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`,
				},
				body: JSON.stringify({ query }),
				signal: controller.signal,
			});
			if (!response.ok) {
				const error = new Error("Failed to load comment activity");
				error.retryable = response.status === 429 || response.status >= 500;
				throw error;
			}

			const result = await response.json();
			if (result.errors) {
				const error = new Error(
					result.errors[0]?.message || "Failed to load comment activity",
				);
				error.retryable = isRetryableGraphqlErrors(result.errors);
				throw error;
			}
			return result.data || {};
		} catch (error) {
			if (signal?.aborted) throw cancelledError();
			lastError =
				error.name === "AbortError"
					? new Error("Timed out loading comment activity")
					: error;
			const canRetry =
				attempt < RETRY_DELAYS_MS.length &&
				(error.name === "AbortError" ||
					error.retryable ||
					error instanceof TypeError);
			if (!canRetry) break;
			await wait(RETRY_DELAYS_MS[attempt]);
		} finally {
			clearTimeout(timeoutId);
			signal?.removeEventListener("abort", cancel);
		}
	}

	throw lastError;
};

// Cancellable requests skip de-duplication: sharing one would let a caller
// that aborts (e.g. an effect re-running) cancel it for everyone else too.
const fetchQuery = (query, token, signal) => {
	if (signal) return requestQuery(query, token, signal);

	const key = JSON.stringify({ query, token });
	if (inFlightQueries.has(key)) return inFlightQueries.get(key);

	const promise = requestQuery(query, token).finally(() => {
		inFlightQueries.delete(key);
	});
	inFlightQueries.set(key, promise);
	return promise;
};

export const runWithConcurrency = async (tasks) => {
	let nextTask = 0;
	const results = new Array(tasks.length);
	const workers = Array.from(
		{ length: Math.min(CONCURRENCY, tasks.length) },
		async () => {
			while (nextTask < tasks.length) {
				const taskIndex = nextTask++;
				results[taskIndex] = await tasks[taskIndex]();
			}
		},
	);
	await Promise.all(workers);
	return results;
};

const loadEarlierDiscussions = async (mrId, connection, token, signal) => {
	const nodes = [...(connection?.nodes || [])];
	let pageInfo = connection?.pageInfo;

	while (pageInfo?.hasPreviousPage) {
		const data = await fetchQuery(
			createPreviousPageQuery(mrId, pageInfo.startCursor),
			token,
			signal,
		);
		const previous = data.mergeRequest?.discussions;
		if (!previous) throw new Error("Incomplete comment activity response");
		nodes.unshift(...(previous.nodes || []));
		pageInfo = previous.pageInfo;
	}

	return nodes;
};

export const chunkMergeRequests = (mrs) => {
	const batches = [];
	for (let index = 0; index < mrs.length; index += DISCUSSION_BATCH_SIZE) {
		batches.push(mrs.slice(index, index + DISCUSSION_BATCH_SIZE));
	}
	return batches;
};

// Fetches every discussion for up to DISCUSSION_BATCH_SIZE MRs, returning one
// fully paged discussion array per MR in input order. Earlier pages load
// sequentially so callers' batch-level concurrency stays the real cap.
export const fetchDiscussionBatch = async (mrs, token, signal) => {
	const data = await fetchQuery(createBatchQuery(mrs), token, signal);
	const connections = mrs.map((_, index) => {
		const connection = data[`mergeRequest${index}`]?.discussions;
		if (!connection) throw new Error("Incomplete comment activity response");
		return connection;
	});
	const discussionsByMr = [];
	for (const [index, mr] of mrs.entries()) {
		discussionsByMr.push(
			await loadEarlierDiscussions(mr.id, connections[index], token, signal),
		);
	}
	return discussionsByMr;
};
